import type {
  PublishedEstimation,
  PublishedEstimationScheme,
  PublishedItemLevel,
  PublishedItemStatus,
  PublishedItemType,
  PublishedTeam,
} from '@tempo-io/roadmap-dto';
import { createContext, type ReactNode, useCallback, useContext, useMemo } from 'react';
import type { EstimationScheme } from '@/common/api/types/estimationScheme';
import type { ItemType, ItemTypeLevel } from '@/common/api/types/itemType';
import type { ItemDetails, ItemDetailsSchedule, PublishedSwimlaneResponse } from './types';

export interface PublishedItemPanelData {
  schedule: ItemDetailsSchedule | null | undefined;
  progress: number | null | undefined;
  resolvedUsers: Record<string, { name: string; pictureUrl: string | null }> | undefined;
  resolvedItems: Record<string, { id: number; name: string; itemTypeId: number }> | undefined;
  resolvedCustomers: Record<string, { id: number; name: string }> | undefined;
}

export interface PublishedContextValue {
  urlToken: string;
  panelItemId?: number;
  itemTypes: ItemType[];
  itemTypeLevels: ItemTypeLevel[];
  itemStatuses: PublishedItemStatus[];
  teams: PublishedTeam[];
  estimations: PublishedEstimation[];
  estimationSchemes: EstimationScheme[];
  getItemPanelData(itemId: number): PublishedItemPanelData | undefined;
  getItemDetails(itemId: number): ItemDetails | undefined;
}

const PublishedContext = createContext<PublishedContextValue | null>(null);

function mapItemTypes(raw: readonly PublishedItemType[]): ItemType[] {
  return raw.map((t) => ({
    id: t.id,
    name: t.name,
    description: null,
    itemLevelId: t.itemLevelId,
    color: t.color ?? null,
    icon: t.icon ?? null,
    createdAt: '',
    updatedAt: '',
    createdBy: '',
    updatedBy: '',
  }));
}

function mapItemTypeLevels(raw: readonly PublishedItemLevel[]): ItemTypeLevel[] {
  return raw.map((l) => ({
    id: l.id,
    organizationId: '',
    level: l.level,
    name: l.name,
    createdAt: '',
    updatedAt: '',
    createdBy: '',
    updatedBy: '',
  }));
}

function mapEstimationSchemes(schemes: readonly PublishedEstimationScheme[]): {
  schemes: EstimationScheme[];
  estimations: PublishedEstimation[];
} {
  const estimations: PublishedEstimation[] = [];
  const mappedSchemes = schemes.map((s) => {
    const mapped = s.estimations.map((e) => ({
      id: e.id,
      organizationId: '',
      label: e.label,
      highEffort: null,
      lowEffort: null,
      createdAt: '',
      updatedAt: '',
      createdBy: '',
      updatedBy: '',
      archivedAt: null,
      estimationSchemeId: e.estimationSchemeId,
    }));
    for (const e of mapped)
      estimations.push({ id: e.id, label: e.label, estimationSchemeId: e.estimationSchemeId });
    return {
      id: s.id,
      organizationId: '',
      name: s.name,
      description: s.description,
      isOrganizationDefault: s.isOrganizationDefault,
      priority: '',
      createdAt: '',
      updatedAt: '',
      createdBy: '',
      updatedBy: '',
      archivedAt: null,
      estimations: mapped,
    };
  });
  return { schemes: mappedSchemes, estimations };
}

export interface PublishedStaticLookups {
  itemTypes: readonly PublishedItemType[];
  itemLevels: readonly PublishedItemLevel[];
  itemStatuses: readonly PublishedItemStatus[];
  teams: readonly PublishedTeam[];
  estimations: readonly PublishedEstimation[];
  estimationSchemes: readonly PublishedEstimationScheme[];
  itemDetails?: Record<string, ItemDetails>;
  resolvedUsers?: PublishedSwimlaneResponse['resolvedUsers'];
  resolvedItems?: PublishedSwimlaneResponse['resolvedItems'];
  resolvedCustomers?: PublishedSwimlaneResponse['resolvedCustomers'];
}

export function PublishedContextProvider({
  urlToken,
  panelItemId,
  staticLookups,
  children,
}: {
  urlToken: string;
  panelItemId?: number;
  staticLookups: PublishedStaticLookups;
  children: ReactNode;
}) {
  const itemTypes = useMemo(() => mapItemTypes(staticLookups.itemTypes), [staticLookups.itemTypes]);
  const itemTypeLevels = useMemo(
    () => mapItemTypeLevels(staticLookups.itemLevels),
    [staticLookups.itemLevels],
  );
  const itemStatuses = useMemo(() => [...staticLookups.itemStatuses], [staticLookups.itemStatuses]);
  const teams = useMemo(() => [...staticLookups.teams], [staticLookups.teams]);
  const { schemes: estimationSchemes, estimations } = useMemo(
    () => mapEstimationSchemes(staticLookups.estimationSchemes),
    [staticLookups.estimationSchemes],
  );

  const itemDetailsMap = staticLookups.itemDetails;
  const resolvedUsers = staticLookups.resolvedUsers;
  const resolvedItems = staticLookups.resolvedItems;
  const resolvedCustomers = staticLookups.resolvedCustomers;

  const getItemPanelData = useCallback(
    (itemId: number): PublishedItemPanelData | undefined => {
      if (!itemDetailsMap) return undefined;
      const d = itemDetailsMap[String(itemId)];
      if (!d) return undefined;
      return {
        schedule: d.schedule,
        progress: d.progress,
        resolvedUsers,
        resolvedItems,
        resolvedCustomers,
      };
    },
    [itemDetailsMap, resolvedUsers, resolvedItems, resolvedCustomers],
  );

  const getItemDetails = useCallback(
    (itemId: number): ItemDetails | undefined => itemDetailsMap?.[String(itemId)],
    [itemDetailsMap],
  );

  const value = useMemo<PublishedContextValue>(
    () => ({
      urlToken,
      panelItemId,
      itemTypes,
      itemTypeLevels,
      itemStatuses,
      teams,
      estimations,
      estimationSchemes,
      getItemPanelData,
      getItemDetails,
    }),
    [
      urlToken,
      panelItemId,
      itemTypes,
      itemTypeLevels,
      itemStatuses,
      teams,
      estimations,
      estimationSchemes,
      getItemPanelData,
      getItemDetails,
    ],
  );

  return <PublishedContext.Provider value={value}>{children}</PublishedContext.Provider>;
}

export function usePublishedContext(): PublishedContextValue | null {
  return useContext(PublishedContext);
}

export function useIsPublishedContext(): boolean {
  return useContext(PublishedContext) !== null;
}
